import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChecksIcon } from '@phosphor-icons/react/dist/csr/Checks';

import { targetPathFor } from './notificationTarget';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { notificationsApi, type NotificationRow } from '../../lib/api/notifications';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './NotificationPanel.module.css';

/**
 * The bell panel (M-16, Task 4, spec §3) — the caller's own notifications,
 * read here for the first time in five milestones of writers.
 *
 * ## `Dialog`, `kind="form"`, not an invented Popover
 *
 * `composites/` has no popover primitive, and adding a second overlay type
 * is a design-system decision this milestone carries no mandate for. The
 * title (`notifications.panelTitle`) NAMES the surface rather than asking a
 * question, which is what `kind="form"` is for — `Dialog`'s own development
 * guard requires exactly that.
 *
 * ## The panel owns the rows; `App.tsx` owns the count
 *
 * The badge lives in the shell and survives the panel being unmounted (it
 * is closed most of the time); the row list lives here and is thrown away
 * on close. `onCountChange` is how the two stay in sync without either one
 * reaching into the other's state.
 *
 * ## `onCountChange` receives a TOTAL, never a delta and never `markAllRead`'s
 * own return value
 *
 * `notificationsApi.markAllRead()` resolves to how many rows IT changed —
 * `0` on a second click, `2` on a first one. `onCountChange` is the
 * caller's new unread TOTAL, which after "mark all read" is always `0`
 * regardless of how many that was. Passing the API's return value straight
 * through would leave the badge showing a stale non-zero count the moment
 * the account actually holds none.
 *
 * A single row's `markRead` has no such envelope to misread — the panel
 * decrements its own running total by one, locally, rather than issuing a
 * second request to `unreadCount()` for a fact it already knows: the row
 * that changed was unread a moment ago and never was more than one.
 *
 * ## `list()`'s own `meta.unread_count` refreshes the badge on open, for free
 *
 * `meta.unread_count` is the account-wide unread total (`notifications.ts`'s
 * own docblock) — reported to `onCountChange` the moment the panel's own
 * list finishes loading, which is what "the badge is fetched … on panel
 * open" (spec §3, no polling) means in practice: opening the panel already
 * makes exactly one request, and that request's own envelope carries the
 * figure the shell needs.
 *
 * ## An unknown `type` renders as text, never a link, and never throws
 *
 * `targetPathFor` returns `null` for a type this map has not learned yet
 * OR one the caller cannot read (`notificationTarget.ts`'s own docblock) —
 * this component draws no distinction between the two: either way, the row
 * is inert text.
 */
export interface NotificationPanelProps {
  open: boolean;
  permissions: readonly string[];
  onClose: () => void;
  /** `setActivePath` from App.tsx — the same setter the nav uses. */
  onNavigate: (path: string) => void;
  /** The caller's new unread total after a mark-read, so App.tsx can correct the badge without a second request. */
  onCountChange: (unreadCount: number) => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function NotificationPanel({ open, permissions, onClose, onNavigate, onCountChange }: NotificationPanelProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<readonly NotificationRow[]>([]);
  const [unreadTotal, setUnreadTotal] = useState(0);

  /**
   * Kept fresh every render, read only from inside effects/handlers — the
   * identical shape `Dialog.tsx`'s own `latest` ref uses to read a current
   * prop without making it a dependency that would re-run a fetch on every
   * parent render (`App.tsx` passes a new closure each time).
   */
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => {
    onCountChangeRef.current = onCountChange;
  });

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setStatus('loading');

    notificationsApi
      .list()
      .then((page) => {
        if (cancelled) return;
        setRows(page.data);
        setUnreadTotal(page.meta.unread_count);
        setStatus('ready');
        onCountChangeRef.current(page.meta.unread_count);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  /**
   * ORDER MATTERS (review round 2, Important 1): mark read, navigate,
   * close. `onClose` was never called at all before this fix — `Dialog`
   * only closes on Escape or its own close button, so a row click
   * delivered the navigation to a screen sitting BEHIND a still-open
   * modal, with the rest of the shell `aria-hidden` underneath it. Firing
   * `markRead` first and reading/writing state through `setRows`/
   * `setUnreadTotal` is safe across the close that follows: `App.tsx`
   * always renders `<NotificationPanel>` and only toggles its `open` prop
   * (`Dialog` itself is what disappears), so this component never
   * unmounts on close and the in-flight request's `.then`/`.catch` still
   * land on a live component.
   */
  function handleRowActivate(target: NotificationRow, path: string): void {
    if (target.read_at === null) {
      void notificationsApi
        .markRead(target.uuid)
        .then((updated) => {
          setRows((current) => current.map((candidate) => (candidate.uuid === updated.uuid ? updated : candidate)));
          setUnreadTotal((current) => {
            const next = Math.max(0, current - 1);
            onCountChangeRef.current(next);
            return next;
          });
        })
        .catch(() => {
          // Best-effort, the `handleMarkAllRead` shape below: a rejection
          // leaves the row unread and the count untouched — the user can
          // retry by clicking the row again. This exists to keep the
          // rejection from going unhandled, not to add error UI for it.
        });
    }

    onNavigate(path);
    onClose();
  }

  async function handleMarkAllRead(): Promise<void> {
    try {
      // DELIBERATELY DISCARDED: how many rows this changed, never the
      // caller's new total — see this component's own docblock.
      await notificationsApi.markAllRead();
    } catch {
      return;
    }

    setRows((current) => current.map((candidate) => (candidate.read_at === null ? { ...candidate, read_at: new Date().toISOString() } : candidate)));
    setUnreadTotal(0);
    onCountChangeRef.current(0);
  }
  return (
    <Dialog
      open={open}
      kind="form"
      title={t('notifications.panelTitle')}
      onClose={onClose}
      actions={
        <Button icon={<ChecksIcon size={16} />} variant="secondary" onClick={() => void handleMarkAllRead()} disabled={unreadTotal === 0}>
          {t('notifications.markAllRead')}
        </Button>
      }
    >
      {status === 'loading' ? <p role="status">{t('list.loading')}</p> : null}

      {status === 'error' ? (
        <div role="alert" className={styles['failure']}>
          <p>{t('notifications.loadFailed')}</p>
        </div>
      ) : null}

      {status === 'ready' && rows.length === 0 ? <p className={styles['empty']}>{t('notifications.empty')}</p> : null}

      {status === 'ready' && rows.length > 0 ? (
        <ul className={styles['list']}>
          {rows.map((notification) => {
            const path = targetPathFor(notification.type, permissions);
            const unread = notification.read_at === null;

            return (
              <li
                key={notification.uuid}
                data-testid={`notification-${notification.uuid}`}
                data-unread={unread ? 'true' : 'false'}
                className={styles['row']}
              >
                {path === null ? (
                  <div className={styles['rowContent']}>
                    <span className={styles['title']}>{notification.title}</span>
                    <span className={styles['body']}>{notification.body}</span>
                    <span className={styles['meta']}>
                      <bdi dir="ltr">{formatTimestamp(notification.created_at ?? '', i18n.language)}</bdi>
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles['rowButton']}
                    onClick={() => {
                      handleRowActivate(notification, path);
                    }}
                  >
                    <span className={styles['title']}>{notification.title}</span>
                    <span className={styles['body']}>{notification.body}</span>
                    <span className={styles['meta']}>
                      <bdi dir="ltr">{formatTimestamp(notification.created_at ?? '', i18n.language)}</bdi>
                    </span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Dialog>
  );
}
